import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import { useCreateRole } from '@/api/mutations/useCreateRole';
import { useDeleteRole } from '@/api/mutations/useDeleteRole';
import { useUpdateRole } from '@/api/mutations/useUpdateRole';
import { useGetRoles, Role } from '@/api/queries/useGetRoles';
import { PERMISSION_GROUPS, type PermissionInfo } from '@/constants/permissionGroups';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Input } from '@/new/components/shadcn/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/new/components/shadcn/tooltip';

// ===== Types =====

interface EditedRole {
  name: string;
  permissions: string[];
}

// ===== Constants =====

const SYSTEM_ROLE_NAMES = new Set(['Administrator', 'Read-only']);

const isSystemRole = (role: Role) => SYSTEM_ROLE_NAMES.has(role.name);

const VALID_PERMISSIONS = new Set(
  PERMISSION_GROUPS.flatMap((g) => g.permissions.filter((p) => !p.delegatedBy).map((p) => p.permission)),
);

const COL_LEFT = 280;
const COL_ROLE = 140;
const COL_ADD = 64;

// ===== Main component =====

export const Roles = () => {
  const { data: rolesData, isLoading } = useGetRoles();
  const roles = useMemo(() => rolesData ?? [], [rolesData]);
  const { mutate: createRole, isLoading: isCreating } = useCreateRole();
  const { mutate: updateRole, isLoading: isUpdating } = useUpdateRole();
  const { mutate: deleteRole, isLoading: isDeleting } = useDeleteRole();

  // Track edits per role: roleId -> { name, permissions }
  const [editedRoles, setEditedRoles] = useState<Record<string, EditedRole>>({});
  const [editingNameRoleId, setEditingNameRoleId] = useState<string | null>(null);
  const [deleteModalRoleId, setDeleteModalRoleId] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Initialize edited state when roles load or change
  useEffect(() => {
    setEditedRoles((prev) => {
      const next: Record<string, EditedRole> = {};
      for (const role of roles) {
        // Keep existing edits if the role hasn't changed server-side
        const existing = prev[role.id];
        if (existing) {
          next[role.id] = existing;
        } else {
          next[role.id] = { name: role.name, permissions: [...role.permissions] };
        }
      }
      return next;
    });
  }, [roles]);

  // Server permissions for highlighting new selections
  const originalRolePermissions = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    for (const role of roles) {
      map[role.id] = new Set(role.permissions);
    }
    return map;
  }, [roles]);

  const sortedRoles = useMemo(
    () =>
      [...roles].sort((a, b) => {
        const aSystem = isSystemRole(a);
        const bSystem = isSystemRole(b);
        if (aSystem && !bSystem) return -1;
        if (!aSystem && bSystem) return 1;
        // Administrator always first among system roles
        if (a.name === 'Administrator') return -1;
        if (b.name === 'Administrator') return 1;
        return a.name.localeCompare(b.name);
      }),
    [roles],
  );

  const isDirty = useCallback(
    (role: Role) => {
      const edited = editedRoles[role.id];
      if (!edited) return false;
      if (edited.name !== role.name) return true;
      const sortedEdited = [...edited.permissions].sort();
      const sortedOriginal = [...role.permissions].sort();
      return JSON.stringify(sortedEdited) !== JSON.stringify(sortedOriginal);
    },
    [editedRoles],
  );

  const dirtyRoles = useMemo(() => sortedRoles.filter((r) => isDirty(r) && !isSystemRole(r)), [sortedRoles, isDirty]);

  const handlePermissionToggle = (roleId: string, permission: string) => {
    setEditedRoles((prev) => {
      const current = prev[roleId];
      if (!current) return prev;
      const has = current.permissions.includes(permission);
      return {
        ...prev,
        [roleId]: {
          ...current,
          permissions: has ? current.permissions.filter((p) => p !== permission) : [...current.permissions, permission],
        },
      };
    });
  };

  const handleGroupToggle = (roleId: string, groupPermissions: string[]) => {
    setEditedRoles((prev) => {
      const current = prev[roleId];
      if (!current) return prev;
      const allSelected = groupPermissions.every((p) => current.permissions.includes(p));
      let newPermissions: string[];
      if (allSelected) {
        newPermissions = current.permissions.filter((p) => !groupPermissions.includes(p));
      } else {
        newPermissions = [...current.permissions];
        for (const p of groupPermissions) {
          if (!newPermissions.includes(p)) {
            newPermissions.push(p);
          }
        }
      }
      return { ...prev, [roleId]: { ...current, permissions: newPermissions } };
    });
  };

  const handleNameChange = (roleId: string, name: string) => {
    setEditedRoles((prev) => {
      const current = prev[roleId];
      if (!current) return prev;
      return { ...prev, [roleId]: { ...current, name } };
    });
  };

  const isNameDuplicate = useCallback(
    (roleId: string, name: string) => {
      const trimmed = name.trim().toLowerCase();
      if (!trimmed) return false;
      return sortedRoles.some((r) => {
        if (r.id === roleId) return false;
        const otherName = (editedRoles[r.id]?.name ?? r.name).trim().toLowerCase();
        return otherName === trimmed;
      });
    },
    [sortedRoles, editedRoles],
  );

  const hasDuplicateNames = useMemo(
    () => sortedRoles.some((r) => !isSystemRole(r) && isNameDuplicate(r.id, editedRoles[r.id]?.name ?? r.name)),
    [sortedRoles, editedRoles, isNameDuplicate],
  );

  const handleSaveAll = () => {
    if (hasDuplicateNames) return;
    for (const role of dirtyRoles) {
      const edited = editedRoles[role.id];
      if (!edited || !edited.name.trim() || edited.name.length > 50) continue;
      updateRole({ roleId: role.id, name: edited.name.trim(), permissions: edited.permissions });
    }
  };

  const handleDiscardAll = () => {
    setEditedRoles((prev) => {
      const next = { ...prev };
      for (const role of dirtyRoles) {
        next[role.id] = { name: role.name, permissions: [...role.permissions] };
      }
      return next;
    });
  };

  const getUniqueName = useCallback(
    (base: string) => {
      const names = new Set(roles.map((r) => r.name.trim().toLowerCase()));
      if (!names.has(base.trim().toLowerCase())) return base;
      let n = 1;
      while (names.has(`${base} (${n})`.toLowerCase())) n++;
      return `${base} (${n})`;
    },
    [roles],
  );

  const handleCreateRole = () => {
    createRole(
      { name: getUniqueName('New Role'), permissions: [] },
      { onSuccess: (newRole) => setEditingNameRoleId(newRole.id) },
    );
  };

  const handleDuplicate = (role: Role) => {
    const permissions = [...role.permissions].filter((p) => (VALID_PERMISSIONS as Set<string>).has(p));
    createRole(
      { name: getUniqueName(`${role.name} (duplicate)`), permissions },
      { onSuccess: (newRole) => setEditingNameRoleId(newRole.id) },
    );
  };

  const handleDeleteConfirm = () => {
    if (!deleteModalRoleId) return;
    deleteRole(deleteModalRoleId, {
      onSuccess: () => setDeleteModalRoleId(null),
    });
  };

  const toggleGroup = (groupName: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const deleteModalRole = roles.find((r) => r.id === deleteModalRoleId);

  if (isLoading) {
    return (
      <div className="w-full py-6">
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded bg-gray-200" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col py-3">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
        {/* Single scrollable container */}
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="border-collapse" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: COL_LEFT }} />
              {sortedRoles.map((role) => (
                <col key={role.id} style={{ width: COL_ROLE }} />
              ))}
              <col style={{ width: COL_ADD }} />
            </colgroup>

            <thead>
              <tr className="sticky top-0 z-20">
                {/* Corner: Permission header — sticky both directions */}
                <th className="sticky left-0 z-30 border-r border-b border-gray-200 px-4 py-3 text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Permission</span>
                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => {
                              const allGroupNames = PERMISSION_GROUPS.map((g) => g.name);
                              const allCollapsed = allGroupNames.every((n) => collapsedGroups.has(n));
                              setCollapsedGroups(allCollapsed ? new Set() : new Set(allGroupNames));
                            }}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <Icon
                              className={`h-3.5 w-3.5 ${
                                PERMISSION_GROUPS.every((g) => collapsedGroups.has(g.name))
                                  ? 'lucide--chevrons-down-up'
                                  : 'lucide--chevrons-up-down'
                              }`}
                            />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {PERMISSION_GROUPS.every((g) => collapsedGroups.has(g.name)) ? 'Expand all' : 'Collapse all'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </th>

                {/* Role headers */}
                {sortedRoles.map((role) => (
                  <RoleColumnHeader
                    key={role.id}
                    role={role}
                    editedName={editedRoles[role.id]?.name ?? role.name}
                    isEditingName={editingNameRoleId === role.id}
                    isSystem={isSystemRole(role)}
                    isDuplicateName={isDirty(role) && isNameDuplicate(role.id, editedRoles[role.id]?.name ?? role.name)}
                    isUpdating={isUpdating}
                    onStartEditingName={() => setEditingNameRoleId(role.id)}
                    onStopEditingName={() => setEditingNameRoleId(null)}
                    onNameChange={(name) => handleNameChange(role.id, name)}
                    onDuplicate={() => handleDuplicate(role)}
                    onDelete={() => setDeleteModalRoleId(role.id)}
                  />
                ))}

                {/* Add role button */}
                <th className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <Button
                          onClick={handleCreateRole}
                          disabled={isCreating}
                          variant="ghost"
                          className="h-8 w-8 p-0"
                        >
                          <Icon className="lucide--plus h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Add new role</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </th>
              </tr>
            </thead>

            <tbody>
              {PERMISSION_GROUPS.map((group) => {
                const groupPermissions = group.permissions.map((p) => p.permission);
                return (
                  <PermissionGroupRows
                    key={group.name}
                    group={group}
                    groupPermissions={groupPermissions}
                    isCollapsed={collapsedGroups.has(group.name)}
                    onToggleCollapse={() => toggleGroup(group.name)}
                    sortedRoles={sortedRoles}
                    editedRoles={editedRoles}
                    originalRolePermissions={originalRolePermissions}
                    onPermissionToggle={handlePermissionToggle}
                    onGroupToggle={handleGroupToggle}
                    isUpdating={isUpdating}
                  />
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Save bar — always visible, actions fade in on change */}
        <div className="border-t border-gray-200 px-4 py-2">
          <div
            className={`flex items-center justify-end gap-3 transition-opacity duration-300 ${dirtyRoles.length > 0 ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
          >
            <span className="text-sm text-gray-600">
              {dirtyRoles.length} unsaved change{dirtyRoles.length !== 1 ? 's' : ''}
            </span>
            <div className="flex gap-2">
              <Button onClick={handleDiscardAll} disabled={isUpdating} variant="ghost" className="h-8 text-sm">
                Discard all
              </Button>
              <Button
                onClick={handleSaveAll}
                disabled={isUpdating || hasDuplicateNames}
                variant="primary"
                className="h-8 text-sm"
              >
                Save all
              </Button>
            </div>
          </div>
        </div>
      </div>
      {deleteModalRole && (
        <ConfirmationModal
          isOpen={Boolean(deleteModalRoleId)}
          onOpenChange={(open) => {
            if (!open) setDeleteModalRoleId(null);
          }}
          title="Delete Role"
          onConfirm={handleDeleteConfirm}
          isLoading={isDeleting}
        >
          <p>
            Are you sure you want to delete the role &quot;{deleteModalRole.name}&quot;? This action cannot be undone.
          </p>
        </ConfirmationModal>
      )}
    </div>
  );
};

// ===== Role column header =====

interface RoleColumnHeaderProps {
  role: Role;
  editedName: string;
  isEditingName: boolean;
  isSystem: boolean;
  isDuplicateName: boolean;
  isUpdating: boolean;
  onStartEditingName: () => void;
  onStopEditingName: () => void;
  onNameChange: (name: string) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const RoleColumnHeader = ({
  role,
  editedName,
  isEditingName,
  isSystem,
  isDuplicateName,
  isUpdating,
  onStartEditingName,
  onStopEditingName,
  onNameChange,
  onDuplicate,
  onDelete,
}: RoleColumnHeaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const canDelete = !isSystem && !role.isDefault && role.userCount === 0;

  useEffect(() => {
    if (isEditingName) {
      inputRef.current?.focus();
    }
  }, [isEditingName]);

  const getDeleteTooltip = () => {
    if (role.isDefault) return 'Default role cannot be deleted';
    if (role.userCount > 0) return "Roles can't be deleted while assigned to users";
    return '';
  };

  const isNameEmpty = editedName.trim().length === 0;
  const isNameInvalid = isNameEmpty || isDuplicateName;

  const handleFinishEditing = () => {
    if (isNameEmpty) {
      onNameChange(role.name);
    }
    onStopEditingName();
  };

  return (
    <th className="min-w-[140px] border-b border-gray-200 bg-gray-50 px-4 py-3 text-center">
      <div className="flex flex-col items-center gap-1">
        {/* Name */}
        {isEditingName && !isSystem ? (
          <div className="w-full">
            <Input
              ref={inputRef}
              value={editedName}
              onChange={(e) => onNameChange(e.target.value)}
              maxLength={50}
              className={`h-7 w-full text-center text-sm font-medium ${isNameInvalid ? 'border-red-500' : ''}`}
              onBlur={handleFinishEditing}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleFinishEditing();
                if (e.key === 'Escape') {
                  onNameChange(role.name);
                  onStopEditingName();
                }
              }}
            />
            {isDuplicateName && <p className="mt-1 text-xs text-red-500">Name already exists</p>}
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1">
              <span className={`text-sm font-medium ${isDuplicateName ? 'text-red-500' : 'text-gray-900'}`}>
                {editedName}
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="text-gray-400 hover:text-gray-600">
                    <Icon className="lucide--more-vertical h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {!isSystem && (
                    <DropdownMenuItem onClick={onStartEditingName}>
                      <Icon className="lucide--pencil-line mr-2 h-3.5 w-3.5" />
                      Rename
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={onDuplicate}>
                    <Icon className="lucide--copy mr-2 h-3.5 w-3.5" />
                    Duplicate
                  </DropdownMenuItem>
                  {!isSystem &&
                    (canDelete ? (
                      <DropdownMenuItem onClick={onDelete} className="text-red-600">
                        <Icon className="lucide--trash-2 mr-2 h-3.5 w-3.5" />
                        Delete
                      </DropdownMenuItem>
                    ) : (
                      <TooltipProvider>
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <DropdownMenuItem disabled className="text-gray-400">
                              <Icon className="lucide--trash-2 mr-2 h-3.5 w-3.5" />
                              Delete
                            </DropdownMenuItem>
                          </TooltipTrigger>
                          <TooltipContent>{getDeleteTooltip()}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {isDuplicateName && <p className="text-xs text-red-500">Name already exists</p>}
          </div>
        )}

        {/* User count + badges */}
        <div className="flex items-center gap-1.5 font-normal">
          <span className="text-xs text-gray-500">
            {role.userCount} {role.userCount === 1 ? 'user' : 'users'}
          </span>
          {role.isDefault && (
            <span className="rounded-full border bg-gray-100 px-1.25 py-0.25 text-[10px] text-gray-500">Default</span>
          )}
          {isSystem && (
            <span className="rounded-full border border-teal-200 bg-teal-50 px-1.25 py-0.25 text-[10px] font-normal text-gray-500 text-teal-500">
              Wexler
            </span>
          )}
        </div>
      </div>
    </th>
  );
};

// ===== Permission group rows (merged label + checkbox) =====

interface PermissionGroupRowsProps {
  group: {
    name: string;
    depth: number;
    permissions: {
      permission: string;
      description: string;
      delegatedDescriptions?: string[];
      delegatedBy?: string[];
    }[];
  };
  groupPermissions: string[];
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  sortedRoles: Role[];
  editedRoles: Record<string, EditedRole>;
  originalRolePermissions: Record<string, Set<string>>;
  onPermissionToggle: (roleId: string, permission: string) => void;
  onGroupToggle: (roleId: string, groupPermissions: string[]) => void;
  isUpdating: boolean;
}

const PermissionGroupRows = ({
  group,
  groupPermissions,
  isCollapsed,
  onToggleCollapse,
  sortedRoles,
  editedRoles,
  originalRolePermissions,
  onPermissionToggle,
  onGroupToggle,
  isUpdating,
}: PermissionGroupRowsProps) => {
  // Only non-delegated permissions participate in group-level toggle
  const editableGroupPermissions = useMemo(
    () => group.permissions.filter((p) => !p.delegatedBy).map((p) => p.permission),
    [group.permissions],
  );

  return (
    <>
      {/* Group header row */}
      <tr className="border-t border-gray-100">
        {/* Left: group name — sticky */}
        <td
          className="sticky left-0 z-10 border-r border-gray-200 bg-gray-50 py-2"
          style={{ paddingLeft: `${16 + (group.depth - 1) * 20}px` }}
        >
          <button
            onClick={onToggleCollapse}
            className="flex items-center gap-2 text-sm font-medium whitespace-nowrap text-gray-900"
          >
            <Icon
              className={`lucide--chevron-right h-3.5 w-3.5 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
            />
            {group.name}
          </button>
        </td>

        {/* Right: group-level checkboxes */}
        {sortedRoles.map((role) => {
          const edited = editedRoles[role.id];
          if (!edited) return <td key={role.id} className="bg-gray-50" />;
          const original = originalRolePermissions[role.id];
          const allSelected = editableGroupPermissions.every((p) => edited.permissions.includes(p));
          const someSelected = !allSelected && editableGroupPermissions.some((p) => edited.permissions.includes(p));
          const system = isSystemRole(role);

          const hasNewInGroup = allSelected && original && editableGroupPermissions.some((p) => !original.has(p));
          return (
            <td key={role.id} className="bg-gray-50 px-4 py-2">
              <div className="flex justify-center">
                <Checkbox
                  checked={allSelected}
                  ref={(el) => {
                    if (el) {
                      const input = el as unknown as HTMLButtonElement;
                      input.dataset.indeterminate = String(someSelected);
                    }
                  }}
                  onCheckedChange={() => onGroupToggle(role.id, editableGroupPermissions)}
                  disabled={system || isUpdating}
                  className={`h-4 w-4 ${
                    allSelected && hasNewInGroup
                      ? 'data-[state=checked]:border-teal-500 data-[state=checked]:bg-teal-500 data-[state=checked]:hover:bg-teal-500/90'
                      : ''
                  }`}
                />
              </div>
            </td>
          );
        })}
        <td className="bg-gray-50" />
      </tr>

      {/* Individual permission rows */}
      {!isCollapsed &&
        group.permissions.map((permissionInfo) => {
          const isDelegated = !!permissionInfo.delegatedBy;
          const parentPermissionLabel = isDelegated ? permissionInfo.delegatedBy!.join(' or ') : null;

          return (
            <tr key={permissionInfo.permission} className="border-t border-gray-100">
              {/* Left: permission label — sticky */}
              <td
                className="sticky left-0 z-10 border-r border-gray-200 bg-white py-1.5 pr-4"
                style={{ paddingLeft: `${39 + (group.depth - 1) * 20}px` }}
              >
                <div className="flex items-center gap-1.5">
                  <span className={`truncate text-sm ${isDelegated ? 'text-gray-400' : 'text-gray-800'}`}>
                    {permissionInfo.permission.split(':')[1]}
                  </span>
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <button className="shrink-0">
                          <Icon
                            className={`h-3 w-3 ${isDelegated ? 'lucide--replace text-gray-400' : 'lucide--info text-gray-400'}`}
                          />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p className="text-xs">{permissionInfo.description}</p>
                        {isDelegated && (
                          <p className="mt-1 text-xs text-gray-400">
                            Controlled by the {parentPermissionLabel} permission
                          </p>
                        )}
                        {permissionInfo.delegatedDescriptions && permissionInfo.delegatedDescriptions.length > 0 && (
                          <>
                            <p className="mt-1 text-xs font-medium">Also grants:</p>
                            <ul className="ml-3 list-disc text-xs">
                              {permissionInfo.delegatedDescriptions.map((d) => (
                                <li key={d}>{d}</li>
                              ))}
                            </ul>
                          </>
                        )}
                        {(permissionInfo as PermissionInfo).isOrgScoped && (
                          <p className="mt-1 text-xs text-gray-400">
                            Organisation-level — not affected by Matter-level permission overrides
                          </p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </td>

              {/* Right: checkboxes */}
              {sortedRoles.map((role) => {
                const edited = editedRoles[role.id];
                if (!edited) return <td key={role.id} className="bg-white" />;
                const original = originalRolePermissions[role.id];
                const system = isSystemRole(role);

                if (isDelegated) {
                  // Delegated: checked if any parent permission is active, always disabled
                  const isChecked = permissionInfo.delegatedBy!.some((parent) => edited.permissions.includes(parent));
                  return (
                    <td key={role.id} className="bg-white px-4 py-1.5">
                      <div className="flex justify-center">
                        <Checkbox checked={isChecked} disabled className="h-4 w-4 opacity-50" />
                      </div>
                    </td>
                  );
                }

                const isChecked = edited.permissions.includes(permissionInfo.permission);
                const isNew = isChecked && original && !original.has(permissionInfo.permission);
                const isRemoved = !isChecked && original && original.has(permissionInfo.permission);

                return (
                  <td key={role.id} className="bg-white px-4 py-1.5">
                    <div className="flex justify-center">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => onPermissionToggle(role.id, permissionInfo.permission)}
                        disabled={system || isUpdating}
                        className={`h-4 w-4 ${
                          isNew
                            ? 'data-[state=checked]:border-teal-500 data-[state=checked]:bg-teal-500 data-[state=checked]:hover:bg-teal-500/90'
                            : isRemoved
                              ? 'border-2 border-red-500'
                              : ''
                        }`}
                      />
                    </div>
                  </td>
                );
              })}
              <td className="bg-white" />
            </tr>
          );
        })}
    </>
  );
};
